import type { SessionAccess } from '@fos/shared/contracts/identity';
import type { ModuleKey, RequiredLevel, Role } from '@fos/shared/enums';
import { useSession } from './auth';

type BooleanFlagKey = Exclude<keyof SessionAccess['flags'], 'approvesAi' | 'aiMonthlyLimitPln'>;

export interface Access {
  role: Role | null;
  dataScope: SessionAccess['dataScope'] | null;
  /** Czy widać moduł (domyślnie podgląd). */
  can(module: ModuleKey, level?: RequiredLevel): boolean;
  /** Flaga logiczna; `approvesAi` = coś innego niż „none”. */
  flag(name: BooleanFlagKey | 'approvesAi'): boolean;
  /** Admin, Manager, Właściciel: przełącznik „Moje / Zespół”. */
  isManagerOrAbove: boolean;
  /** Właściciel i Admin: zarządzanie osobami i rolami. */
  isAdmin: boolean;
}

/** Zwraca funkcje dostępu dla danego profilu (czysta funkcja: łatwa do testów). */
export function accessFrom(access: SessionAccess | null, role: Role | null): Access {
  return {
    role,
    dataScope: access?.dataScope ?? null,
    can(module, level = 'view') {
      const current = access?.modules[module] ?? 'none';
      return level === 'edit' ? current === 'edit' : current !== 'none';
    },
    flag(name) {
      if (!access) return false;
      if (name === 'approvesAi') return access.flags.approvesAi !== 'none';
      return access.flags[name] === true;
    },
    isManagerOrAbove: role === 'owner' || role === 'admin' || role === 'manager',
    isAdmin: role === 'owner' || role === 'admin',
  };
}

/**
 * Uprawnienia z sesji. Służą tylko do ukrywania elementów UI; backend i tak sprawdza dostęp
 * (CONVENTIONS §10).
 */
export function useAccess(): Access {
  const { data } = useSession();
  if (!data?.authenticated) return accessFrom(null, null);
  return accessFrom(data.access, data.membership?.role ?? null);
}
